// MP-OS-188 bug #5 — Sales Trends' active filters (segment, brand comparison, strength,
// format, category, flavour, nicotine) and, since round 3, every other control on the page
// (range, Group by, partial periods, indexed, the overlay toggles) lived only in LandingHero's React state. Navigating to
// another section (Top lists, Market, …) and back unmounts that component — App.tsx only keeps
// it mounted across the Overview↔Trends toggle, not across other sections — so every one of
// those choices silently reverted, without the user ever pressing Reset.
//
// Persist them in sessionStorage instead, keyed by the signed-in user AND the active supplier
// scope, so a shared device or an admin's "view as supplier" switch never resurrects another
// account's or another scope's filters. Pure so the keying/parsing rules are unit-tested
// without mounting the dashboard.

export interface TrendFilterState {
  segment: string
  requestedSegment: string
  strength: string
  fmt: string
  category: string
  flavour: string
  nicotine: string
  brandSel: string[]
  perBrand: boolean
  // MP-OS-188 round 3 — every other Sales Trends control (range, Group by, partial periods,
  // indexed, cumulative/rolling/comparison, …). Optional and validated field by field on
  // restore (sanitizeTrendView), so one stale or tampered value falls back to its default
  // without discarding the rest — and a selection saved before this field existed still loads.
  view?: Partial<TrendViewState>
}

export type TrendFreq = 'day' | 'week' | 'month'
export type TrendWindowState =
  | { kind: 'preset'; n: number }
  | { kind: 'ytd' }
  | { kind: 'custom'; from: string; to: string }

export interface TrendViewState {
  freq: TrendFreq
  win: TrendWindowState
  showCustom: boolean
  showPartial: boolean
  indexed: boolean
  acc: boolean
  avg: boolean
  cmpPrev: boolean
  rollOverride: number | null
  showAdv: boolean
}

export interface TrendViewContext {
  /** Latest date with data — a custom range may not end after it. */
  dataEnd: string
  /** Earliest date with data — a custom range may not start before it. */
  historyFrom: string
  /** The Range presets the data can fill for a granularity (usableWindowPresets). */
  presetsFor: (freq: TrendFreq) => readonly number[]
  /** Longest custom span allowed, in days. */
  maxCustomSpanDays: number
}

const FREQS: readonly TrendFreq[] = ['day', 'week', 'month']
const BOOLEAN_VIEW_KEYS = ['showCustom', 'showPartial', 'indexed', 'acc', 'avg', 'cmpPrev', 'showAdv'] as const
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const validIso = (v: unknown): v is string =>
  typeof v === 'string' && ISO_DATE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`))
  && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000)

/**
 * Keep only the restored view values that are still valid for the data on screen now.
 * Anything else (wrong type, a preset the data can no longer fill, a custom range outside the
 * available history or longer than the custom-span limit, a rolling window outside 2–26)
 * is dropped, so the control falls back to its default instead of requesting a range the UI
 * would never let the user pick.
 */
export function sanitizeTrendView(raw: unknown, ctx: TrendViewContext): Partial<TrendViewState> {
  if (!raw || typeof raw !== 'object') return {}
  const v = raw as Record<string, unknown>
  const out: Partial<TrendViewState> = {}
  if (FREQS.includes(v.freq as TrendFreq)) out.freq = v.freq as TrendFreq
  // A preset is only meaningful for the granularity it was picked under.
  const freq = out.freq ?? 'week'
  const win = v.win as Record<string, unknown> | null | undefined
  if (win && typeof win === 'object') {
    if (win.kind === 'ytd') out.win = { kind: 'ytd' }
    else if (win.kind === 'preset' && typeof win.n === 'number' && ctx.presetsFor(freq).includes(win.n)) {
      out.win = { kind: 'preset', n: win.n }
    } else if (win.kind === 'custom' && validIso(win.from) && validIso(win.to)
      && win.from <= win.to && win.from >= ctx.historyFrom && win.to <= ctx.dataEnd
      && daysBetween(win.from, win.to) <= ctx.maxCustomSpanDays) {
      out.win = { kind: 'custom', from: win.from, to: win.to }
    }
  }
  for (const key of BOOLEAN_VIEW_KEYS) if (typeof v[key] === 'boolean') out[key] = v[key] as boolean
  // Cumulative and rolling average are mutually exclusive in the UI; never restore both.
  if (out.acc && out.avg) { delete out.acc; delete out.avg }
  if (v.rollOverride === null) out.rollOverride = null
  else if (typeof v.rollOverride === 'number' && Number.isInteger(v.rollOverride)
    && v.rollOverride >= 2 && v.rollOverride <= 26) out.rollOverride = v.rollOverride
  return out
}

const PREFIX = 'mp_trend_filters:'

/** Key by user + scope — never just scope (another user, same supplier) or just user
 *  (an admin's "view as supplier" must not leak into another scope). */
export function trendFilterKey(username: string | null | undefined, scope: string): string {
  return `${PREFIX}${username?.trim() || 'anon'}:${scope || 'market'}`
}

function isTrendFilterState(value: unknown): value is TrendFilterState {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  const strings: (keyof TrendFilterState)[] = ['segment', 'requestedSegment', 'strength', 'fmt', 'category', 'flavour', 'nicotine']
  return strings.every((k) => typeof v[k] === 'string')
    && Array.isArray(v.brandSel) && v.brandSel.every((b) => typeof b === 'string')
    && typeof v.perBrand === 'boolean'
}

export function loadTrendFilters(key: string): TrendFilterState | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isTrendFilterState(parsed) ? parsed : null
  } catch {
    // Corrupted or inaccessible storage must never break the dashboard — fall back to defaults.
    return null
  }
}

export function saveTrendFilters(key: string, state: TrendFilterState): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(key, JSON.stringify(state))
  } catch {
    /* storage unavailable (private mode, quota) — the selection just won't persist */
  }
}

/** Sign-out must never leave a reachable trail of ANY user's or scope's filters behind. */
export function clearAllTrendFilters(): void {
  if (typeof sessionStorage === 'undefined') return
  const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i))
    .filter((k): k is string => !!k && k.startsWith(PREFIX))
  keys.forEach((k) => sessionStorage.removeItem(k))
}
